import { Button, Col, Modal, notification, Row, Table, Tag, Space, Tooltip } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { coreApi } from "../api/calls";
import parse from "html-react-parser";
import moment from "moment/moment";
import BuildingContactFilterPanel from "./Buildings/BuildingContactFilterPanel.jsx";
import BuildingAddCommunication from "./Buildings/BuildingAddCommunication";
import BuildingEditCommunication from "./Buildings/BuildingEditCommunication";
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined, ExclamationCircleOutlined, EyeTwoTone } from "@ant-design/icons";
import { Link } from "react-router-dom";

/**
 * Page with all sent emails
 * @component
 * @alias CommunicationPane
 * @returns <React.Fragment /> with content
 */
const CommunicationPane = ({ currentTabKey = '', building = null }) => {
  const { t } = useTranslation();

  const { id } = useParams();

  const [emails, setEmails] = useState([]);
  const [email, setEmail] = useState({});
  const [pagination, setPagination] = useState({});
  const [queryParams, setQueryParams] = useState({ 
    page: 1,
    cases: ['phone', 'email', 'action']
  });
  const [loading, setDataLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRowKey, setEditingRowKey] = useState(null);
  const [rawValue, setRawValue] = useState(null);

  const fetchEmails = useCallback(() => {
    setDataLoading(true);

    coreApi
      .get(`/emails/buildings/` + id, { params: queryParams })
      .then((response) => {
        let { data, ...pagination } = response.data;

        setEmails(data);
        setPagination(pagination);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        setDataLoading(false)
      });
  }, [queryParams]);

  const getActivePartner = () => {
    if (!building || !building.building_units || building.building_units.length === 0) {
      return null;
    }

    for (const unit of building.building_units) {
      if (unit.service_rrules && unit.service_rrules.length > 0) {
        const lastRrule = unit.service_rrules[unit.service_rrules.length - 1];
        if (lastRrule?.partner?.id) {
          return lastRrule.partner.id;
        }
      }
    }

    if (emails && emails.length > 0) {
      return emails[0]?.partner_id || null;
    }

    return null;
  };

  const updateEmail = (record) => {
    setDataLoading(true);
    if(!record.building_id) {
      record.building_id = id;
    }
    coreApi
      .put("/emails/" + record.id, record)
      .catch((error) => {
        notification.error({ message: error?.response?.data?.message });
      }).finally(() => {
      setDataLoading(false)
    });
  };

  const deleteCommunication = (record) => {
    if (!record.id) {
      notification.error({ message: t("common.error") + ": Missing record ID" });
      return;
    }

    Modal.confirm({
      title: t("emails.communication_delete_title"),
      content: t("emails.communication_delete_content"),
      icon: <ExclamationCircleOutlined />,
      okText: t("common.delete"),
      okButtonProps: { type: "danger" },
      onOk: () => {
        setDataLoading(true);
        coreApi
          .delete("/emails/" + record.id)
          .then(() => {
            notification.success({ message: t("common.recordDeleted") });
            fetchEmails();
          })
          .catch((error) => {
            const data = error?.response?.data;

            if (error?.response?.status === 409 && data?.building_id) {
              const claimLink = data.building_unit_id
                ? `/buildings/${data.building_id}/unit/${data.building_unit_id}#claims`
                : `/buildings/${data.building_id}#claims`;

              notification.error({
                message: data.message,
                description: <a href={claimLink} target={'_blank'}>{t("emails.go_to_claim")}</a>,
                duration: 0,
              });
              return;
            }

            notification.error({ message: data?.message || t("common.deletionFailed") });
          })
          .finally(() => {
            setDataLoading(false);
          });
      },
    });
  };

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  useEffect(() => {
    if (currentTabKey === '#communication') {
      fetchEmails();
    }
    return () => {}
  }, [currentTabKey]);

  const handleSave = (record) => {
    if (record.body_html !== rawValue) {
      record.body_html = rawValue;
      updateEmail(record);
    }
    handleCancel();
  };

  const handleEdit = (record) => {
    setEditingRowKey(record.id)
    setRawValue(record.body_html || record.subject);
  };

  const isEditing = (record) => record.id === editingRowKey;

  const handleCancel = () => {
    setEditingRowKey(null);
    setRawValue(null);
  };

  function cleanText(input) {
    if (!input) return '';
    return input
      .replace(/(@media[\s\S]*?\{[\s\S]*?})/g, "")
      .replace(/(\.\w+[\s\S]*?\{[\s\S]*?})/g, "")
      .replace(/@[\s\S]*?}/g, "")
      .replace(/^[\s}]+/g, "")
      .replace(/\n\s*\n+/g, "\n")
      .trim();
  }

  /**
   * Converts order numbers in text to clickable links
   * @param {string} text - Text that may contain "Změna v objednávce" with order number
   * @returns {JSX.Element|string} - Text with order number as link or original text
   */
  const convertOrderNumberToLink = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Check if text contains "Změna v objednávce"
    if (text.includes('Změna v objednávce')) {
      // Extract the first number (with leading zeros) from the text
      const numberMatch = text.match(/\d+/);
      
      if (numberMatch) {
        const fullNumber = numberMatch[0]; // Full number with leading zeros (e.g., "00123")
        const orderId = parseInt(fullNumber, 10); // Parse to integer for URL (e.g., 123)
        
        // Split text by the full number to insert link
        const parts = text.split(fullNumber);
        
        return (
          <span>
            {parts[0]}
            <Link to={`/orders/${orderId}`} target="_blank">
              {fullNumber}
            </Link>
            {parts.slice(1).join(fullNumber)}
          </span>
        );
      }
    }

    return text;
  };

  const getTypeColor = (typeKey) => {

    if (['invoice', 'finance', 'reminder', 'contract', 'balance', 'document'].includes(typeKey)) {
      return 'green';
    }

    if (['other'].includes(typeKey)) {
      return 'default';
    }

    if (['operational', 'review', 'schedule', 'clientSchedule', 'partnerSchedule', 'partnerScheduleManual', 'info', 'substitute', 'partnerSubstitute', 'clientSubstitute', 'offerSubstitute', 'clientPartnerChange', 'cleaningDayChange', 'cleaningTimeChange', 'clientSubstitute'].includes(typeKey)) {
      return 'orange';
    }

    if (['verification', 'claim'].includes(typeKey)) {
      return 'red';
    }

    return 'default';
  };

  const isEditableRecord = (record) => {
    const communication_cases = ['phone', 'email'];
    const communications = ['review', 'verification', 'finance', 'operational', 'contract', 'other'];

    let hasValidCase = false;
    if (record.case && communication_cases.includes(record.case)) {
      hasValidCase = true;
    }
    if (!hasValidCase && record.tags && record.tags.length > 0) {
      const caseTags = record.tags.filter(tag => tag.type === 'communication_case');
      hasValidCase = caseTags.some(tag => communication_cases.includes(tag.key));
    }

    let hasValidType = false;
    if (record.type && communications.includes(record.type)) {
      hasValidType = true;
    }
    if (!hasValidType && record.tags && record.tags.length > 0) {
      const typeTags = record.tags.filter(tag => tag.type === 'communication_type');
      hasValidType = typeTags.some(tag => communications.includes(tag.key));
    }

    return hasValidCase && hasValidType;
  };

  const isDeletableRecord = (record) => {
    // System messages that cannot be deleted
    const systemTypes = [
      'invoice', 'document', 'reminder', 'clientSchedule', 
      'clientSubstitute', 'clientPartnerChange', 'cleaningDayChange', 'cleaningTimeChange', 'info'
    ];

    let hasSystemType = false;
    if (record.type && systemTypes.includes(record.type)) {
      hasSystemType = true;
    }
    if (!hasSystemType && record.tags && record.tags.length > 0) {
      const typeTags = record.tags.filter(tag => tag.type === 'communication_type');
      hasSystemType = typeTags.some(tag => systemTypes.includes(tag.key));
    }

    return !hasSystemType;
  };

  const isActionCaseWithException = (record) => {
    const exceptionTypes = ['cleaningDayChange', 'clientPartnerChange', 'clientSubstitute', 'cleaningTimeChange'];

    const isActionCase = record.case === 'action' || 
      (record.tags && record.tags.some(tag => tag.type === 'communication_case' && tag.key === 'action'));
    
    if (!isActionCase) {
      return false;
    }

    const hasExceptionType = (record.type && exceptionTypes.includes(record.type)) ||
      (record.tags && record.tags.some(tag => tag.type === 'communication_type' && exceptionTypes.includes(tag.key)));

    return !hasExceptionType;
  };

  const columns = [
    {
      title: t("emails.date"),
      key: "created_at",
      dataIndex: "created_at",
      width: "8%",
      render: (rowData) => moment(rowData).format("DD.MM.YYYY"),
    },
    {
      title: t("emails.communication"),
      key: "case",
      width: "10%",
      render: (_, record) => {
        const allCaseTags = [];

        if (record.case && record.case !== 'null') {
          const translationKey = "emails.communication_cases." + record.case;
          const translatedText = t(translationKey);
          allCaseTags.push({
            id: `case-${record.case}`,
            key: record.case,
            name: translatedText !== translationKey ? translatedText : record.case,
            isOld: true
          });
        }

        if (record.tags && record.tags.length > 0) {
          const caseTags = record.tags.filter(tag => tag.type === 'communication_case');
          caseTags.forEach(tag => {

            if (tag.key && tag.key !== 'null' && !allCaseTags.some(t => t.key === tag.key)) {
              allCaseTags.push({
                id: tag.id,
                key: tag.key,
                name: tag.translation?.tag_name || tag.key,
                isOld: false
              });
            }
          });
        }

        if (allCaseTags.length === 0) {
          return <Tag>-</Tag>;
        }

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {allCaseTags.map((tag) => (
              <Tag key={tag.id} color="blue">
                {tag.name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: t("emails.communication_type"),
      key: "type",
      width: "10%",
      render: (_, record) => {
        const allTypeTags = [];

        if (record.type && record.type !== 'null') {
          const translationKey = "emails.types." + record.type;
          const translatedText = t(translationKey);
          allTypeTags.push({
            id: `type-${record.type}`,
            key: record.type,
            name: translatedText !== translationKey ? translatedText : record.type,
            isOld: true
          });
        }

        if (record.tags && record.tags.length > 0) {
          const typeTags = record.tags.filter(tag => tag.type === 'communication_type');
          typeTags.forEach(tag => {
            if (tag.key && tag.key !== 'null' && !allTypeTags.some(t => t.key === tag.key)) {
              allTypeTags.push({
                id: tag.id,
                key: tag.key,
                name: tag.translation?.tag_name || tag.key,
                isOld: false
              });
            }
          });
        }

        if (allTypeTags.length === 0) {
          return <Tag>-</Tag>;
        }

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {allTypeTags.map((tag) => (
              <Tag key={tag.id} color={getTypeColor(tag.key)}>
                {tag.name}
              </Tag>
            ))}
          </div>
        );
      },
    },
    {
      title: t("emails.with"),
      key: "to_full",
      dataIndex: "to_full",
      width: "10%",
    },
    {
      title: t("invoices.manager"),
      key: "from_full",
      width: "10%",
      render: (rowData) => {
        return rowData.from_full ?? t("common.system");
      },
    },
    {
      title: t("partners.partner"),
      key: "partner_name",
      width: "10%",
      render: (rowData) => {
        return rowData.partner_id ? (
          <Link to={"/partners/" + rowData.partner_id} key={rowData.partner_id} target={"_blank"}>
            {rowData.partner_name}
          </Link>
        ) : rowData.partner_name || '-';
      },
    },
    {
      title: t("emails.subject"),
      key: "subject",
      width: "15%",
      render: (rowData, record) => {
        const hasContractType = (record.type === 'contract') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'contract'));

        const hasClaimType = (record.type === 'claim') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'claim'));

        const dashSubjectTypes = ['other', 'review', 'verification', 'finance', 'operational', 'contract'];
        const hasDashSubjectType = (record.type && dashSubjectTypes.includes(record.type)) ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && dashSubjectTypes.includes(tag.key)));

        if (hasContractType || hasClaimType || hasDashSubjectType) {
          return '-';
        }

        if (isActionCaseWithException(record)) {
          return record.subject || '-';
        }

        return record.subject || '-';
      },
    },
    {
      title: t("emails.description"),
      key: "description",
      width: "17%",
      render: (rowData, record) => {
        const hasContractType = (record.type === 'contract') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'contract'));

        const hasClaimType = (record.type === 'claim') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'claim'));

        const bodyHtmlTypes = ['other', 'review', 'verification', 'finance', 'operational'];
        const hasBodyHtmlType = (record.type && bodyHtmlTypes.includes(record.type)) ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && bodyHtmlTypes.includes(tag.key)));

        const noBodyTypes = [
          'invoice', 'document', 'reminder', 'clientSchedule', 'balance',
          'partnerBuildingSwitch', 'partnerSubstitute', 'offerPermanent',
          'offerSubstitute', 'partnerScheduleManual', 'partnerSchedule', 'info', 'schedule'
        ];

        const hasNoBodyType = (record.type && noBodyTypes.includes(record.type)) ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && noBodyTypes.includes(tag.key)));

        if (hasNoBodyType) {
          return '-';
        }

        let plainText = record.body_html ? record.body_html.replace(/<\/?[^>]+(>|$)/g, '') : '';
        let displayText = cleanText(record.description || plainText || record.subject || '');
        if (hasContractType || hasClaimType) {
          return convertOrderNumberToLink(displayText) || '-';
        }

        if (hasBodyHtmlType) {
          return isEditing(record) ? (
            <div
              contentEditable
              suppressContentEditableWarning
              id={record.id}
              onInput={(e) => setRawValue(e.target.textContent)}
              style={{
                cursor: "text",
                border: "1px solid #1890ff",
              }}
            >
              {displayText}
            </div>
          ) : (
            convertOrderNumberToLink(displayText) || '-'
          );
        }

        if (isActionCaseWithException(record)) {
          return '-';
        }

        return isEditing(record) ? (
          <div
            contentEditable
            suppressContentEditableWarning
            id={record.id}
            onInput={(e) => setRawValue(e.target.textContent)}
            style={{
              cursor: "text",
              border: "1px solid #1890ff",
            }}
          >
            {displayText}
          </div>
        ) : (
          convertOrderNumberToLink(displayText) || '-'
        );
      },
    },
    {
      key: "action",
      width: "10%",
      render: (_, record) => {
        const hasContractType = (record.type === 'contract') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'contract'));

        const hasClaimType = (record.type === 'claim') ||
          (record.tags && record.tags.some(tag => tag.type === 'communication_type' && tag.key === 'claim'));

        if (hasContractType) {
          return '-';
        }

        if (hasClaimType) {
          return (
            <Button
              icon={<DeleteOutlined />}
              danger
              size="small"
              onClick={() => deleteCommunication(record)}
            />
          );
        }

        return isEditing(record)
          ? <Space>
            <Button icon={<CheckOutlined />} onClick={() => handleSave(record)} size="small" />
            <Button danger icon={<CloseOutlined />} onClick={handleCancel} size="small" />
          </Space>
          : <Space>
            <Tooltip title={t("emails.view_email")}>
              <Button
                icon={<EyeTwoTone />}
                size="small"
                onClick={() => {
                  setEmail(record);
                  setIsModalVisible(true);
                }}
              />
            </Tooltip>
            {isEditableRecord(record) ? (
              <BuildingEditCommunication
                record={record}
                buildingId={id}
                defaultPartnerId={getActivePartner()}
                onUpdate={fetchEmails}
              />
            ) : (
              <Tooltip title={t("emails.system_communication_not_editable")}>
                <Button
                  icon={<EditOutlined />}
                  size="small"
                  disabled
                />
              </Tooltip>
            )}
            {isDeletableRecord(record) ? (
              <Button
                icon={<DeleteOutlined />}
                danger
                size="small"
                onClick={() => deleteCommunication(record)}
              />
            ) : (
              <Tooltip title={t("emails.system_communication_not_deletable")}>
                <Button
                  icon={<DeleteOutlined />}
                  danger
                  size="small"
                  disabled
                />
              </Tooltip>
            )}
          </Space>;
      },
    }
  ];

  return (
    <React.Fragment>
      <Row>
        <Col span={24} gutter={[16, 16]} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <BuildingContactFilterPanel
            filters={queryParams}
            setFilters={(newFilters) => {
              setQueryParams({ ...newFilters, page: 1 });
            }}
            fetchEmails={fetchEmails}
            t={t}
            context="building"
          />
          <BuildingAddCommunication id={id} onUpdate={fetchEmails} defaultPartnerId={getActivePartner()}></BuildingAddCommunication>
        </Col>
      </Row>
      <Table
        id="communication_table"
        size={"small"}
        loading={loading}
        dataSource={emails}
        rowKey="id"
        pagination={{
          ...pagination,
          current: queryParams.page,
          showSizeChanger: true,
          onChange: (e, perPage) => {
            setQueryParams({ ...queryParams, page: e, perPage: perPage });
          },
          showTotal: (total, range) => (
            <p>
              {t("common.paginationPartOne")} <strong>{range[0]}</strong>-<strong>{range[1]}</strong>
              {" " + t("common.paginationPartTwo") + " "}
              <strong>{total}</strong> {t("common.paginationPartThree")}
            </p>
          ),
        }}
        columns={columns}
        onChange={(e) => {
          console.log(e);
        }}
      />

      <Modal
        width={1000}
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
      >
        <div className="email-preview-wrapper">
          {email.body_html?.length === 0 ? (
            email.subject
          ) : (
            email.body_html && parse(email.body_html)
          )}
        </div>
        <style jsx>{`
          .email-preview-wrapper p,
          .email-preview-wrapper span,
          .email-preview-wrapper strong,
          .email-preview-wrapper a {
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            word-break: break-word !important;
            white-space: normal !important;
          }
        `}</style>
      </Modal>
    </React.Fragment>
  );
};

export default CommunicationPane;
